import { useMemo } from 'react';
import { FormattedList, FormattedMessage, useIntl } from 'react-intl';

import { getLocalizedColorName } from '@/i18n/getLocalizedColorName';
import { getHardcodedCarModels, Make, Modification } from '@/modules/car/api/hardcoded-make-models';
import type { TMake } from '@src-common/api/definitions/w4d-cars-types';

import { Item as VehicleRequirementItem } from '../Form';

interface ClassesMap {
  tag: Modification['tag'];
  models: Array<Modification['model']>;
  maxAge: Modification['maxAge'];
  colors: Modification['color'];
}

type ListOfServiceClasses = Record<string, ClassesMap>;
type ListOfMakes = Record<TMake, ListOfServiceClasses>;

const createListOfMakesByVehicles = (vehicles: Make[]): ListOfMakes => {
  const listOfMakes = {} as ListOfMakes;

  vehicles.forEach(vehicle => {
    const currentMake: ListOfServiceClasses = {};

    vehicle.modifications.forEach(modification => {
      const currentClass = modification.serviceClass;

      if (!currentMake.hasOwnProperty(currentClass)) {
        currentMake[currentClass] = {
          tag: modification.tag,
          models: [modification.model],
          maxAge: modification.maxAge,
          colors: modification.color,
        };
      } else {
        currentMake[currentClass].models.push(modification.model);
        currentMake[currentClass].maxAge = Math.min(
          currentMake[currentClass].maxAge,
          modification.maxAge,
        );

        const existingColorValues = new Set(
          currentMake[currentClass].colors.map(color => color.value),
        );

        modification.color.forEach(colorItem => {
          if (!existingColorValues.has(colorItem.value)) {
            currentMake[currentClass].colors = [...currentMake[currentClass].colors, colorItem];
            existingColorValues.add(colorItem.value);
          }
        });
      }
    });
    listOfMakes[vehicle.make as TMake] = currentMake;
  });

  return listOfMakes;
};

export const useVehicleByClassList = (cityName: string) => {
  const vehicles = getHardcodedCarModels(cityName);

  const intl = useIntl();

  const listOfMakes = useMemo(() => createListOfMakesByVehicles(vehicles), [vehicles]);

  return useMemo(() => {
    const result: VehicleRequirementItem[] = [];

    for (const [make, serviceClasses] of Object.entries(listOfMakes)) {
      Object.values(serviceClasses).forEach(serviceClass => {
        result.push({
          title: `${make} ${intl.formatList(serviceClass.models, {
            type: 'disjunction',
          })}`,
          description: (
            <>
              <div>
                <FormattedMessage
                  defaultMessage="No older than {count} years"
                  id="uT53ZW"
                  description="Maximum age description"
                  values={{ count: serviceClass.maxAge }}
                />
                <span>*</span>
              </div>
              <div>
                <FormattedList
                  value={serviceClass.colors.map(color => getLocalizedColorName(color.value, intl))}
                  type="disjunction"
                />
              </div>
            </>
          ),
          value: `${make.toLowerCase()}_${serviceClass.tag}`,
        });
      });
    }

    result.push({
      title: intl.formatMessage({
        defaultMessage: "I don't have any of these vehicles yet",
        id: 'bMMVvM',
        description: 'Vehicle option: None',
      }),
      value: 'None',
    });

    return result;
  }, [intl, listOfMakes]);
};
